import React, { FC, useState, useContext } from 'react';
import Close from '@mui/icons-material/Close';
import NotificationsNoneOutlined from '@mui/icons-material/NotificationsNoneOutlined';
import { Flex } from '@dripcapital/dripui-v1/components/Flex';
import { Button } from '@dripcapital/dripui-v1/components/Button';
import {
  Dialog,
  DialogContent,
} from '@dripcapital/dripui-v1/components/Dialog';
import { Typography } from '@dripcapital/dripui-v1/components/Typography';
import { DAJ_COLORS, formatters, handleError, handleSuccess } from 'utils';
import { Spacer } from 'atoms';
import SideBorderCard from 'components/SideBorderCard';
import { useTranslation } from 'react-i18next';
import { sendLeadNotification } from 'api';
import { useReactiveVar } from '@apollo/client';
import { appDataVar } from 'config/server';
import { AnalyticsContext } from 'shared/context/AnalyticsContext';

type Props = {
  mailSent?: boolean;
  mailDate?: any;
  useAsSideCard?: boolean;
  refetchApplicationDetails: () => void;
};

const NotifyLead: FC<Props> = ({
  mailSent = false,
  useAsSideCard = true,
  refetchApplicationDetails,
  mailDate = '',
}) => {
  const { t } = useTranslation();

  const { businessId } = useReactiveVar(appDataVar);

  const { mixpanel } = useContext(AnalyticsContext);

  const [notifyModal, setNotifyModal] = useState<boolean>(false);

  const closeNotifyModal = () => {
    setNotifyModal(false);
  };

  const openNotifyModal = () => {
    setNotifyModal(true);
  };

  const renderNotifyModal = () => {
    const sendNotification = async () => {
      try {
        await sendLeadNotification(businessId);
        mixpanel.track('lead-notification-sent-success', {
          businessId: businessId,
        });
        handleSuccess(t('usa_cp_notify_success'));
      } catch (e) {
        await sendLeadNotification(businessId);
        mixpanel.track('lead-notification-sent-failed', {
          businessId: businessId,
        });
        handleError(t('usa_cp_notify_failed'));
      } finally {
        refetchApplicationDetails();
        setNotifyModal(false);
      }
    };

    return (
      <Dialog open={notifyModal} onClose={closeNotifyModal}>
        <DialogContent>
          <Close
            color="secondary"
            sx={{
              color: DAJ_COLORS.GREEN,
              float: 'right',
              top: 0,
              right: 0,
            }}
            onClick={closeNotifyModal}
          />
          <Spacer verticalSpace={4} />
          <Typography
            variant="h3"
            fontWeight="bold"
            color="secondary"
            _sx={{ textAlign: 'center' }}
          >
            {t('usa_cp_notify_modal_title')}
          </Typography>
          <Spacer verticalSpace={2} />
          <Typography variant="label" _sx={{ textAlign: 'center' }}>
            {t('usa_cp_notify_modal_subtitle')}
          </Typography>
          <Spacer verticalSpace={2} />
          <Flex _sx={{ textAlign: 'center' }} justifyContent={'space-around'}>
            <Button size="sm" variant="contained" onClick={sendNotification}>
              {t('usa_cp_notify_modal_confirm_btn')}
            </Button>
            <Button size="sm" variant="outlined" onClick={closeNotifyModal}>
              {t('usa_cp_notify_modal_cancel_btn')}
            </Button>
          </Flex>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <>
      {renderNotifyModal()}
      <SideBorderCard>
        <Typography
          variant="body"
          _sx={{ lineHeight: '22px' }}
          color="secondary"
          fontWeight="bold"
        >
          {mailSent
            ? t('usa_cp_notify_lead_sent_title', {
                date: formatters.formatToUSDate(mailDate),
              })
            : t('usa_cp_notify_lead_title')}
        </Typography>
        <Spacer verticalSpace={1.5} />
        <Button
          size="sm"
          variant="outlined"
          startIcon={<NotificationsNoneOutlined fontSize="medium" />}
          onClick={openNotifyModal}
          _sx={{ width: useAsSideCard ? '100%' : '192px' }}
        >
          {t(mailSent ? 'usa_cp_notify_again_btn' : 'usa_cp_notify_lead_btn')}
        </Button>
      </SideBorderCard>
    </>
  );
};

export default NotifyLead;
